import React, { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import PayrollLayout from "../../layouts/PayrollLayout";

export default function PayrollSummary() {
  const API_URL = "https://hrmsnew.prepstripe.com/api";
  const token = getToken();

  const api = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  // Filters
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState(
    (new Date().getMonth() + 1).toString().padStart(2, "0"),
  );

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = {
        year: parseInt(year),
        month: parseInt(month),
      };

      const response = await api.get("/payroll/summary", { params });
      setSummary(response.data);
    } catch (err) {
      console.error("Error loading summary:", err);
      setError(err.response?.data?.message || "Failed to load payroll summary");
    } finally {
      setLoading(false);
    }
  }, [api, year, month]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
      processed:
        "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      approved:
        "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      rejected: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
      paid: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
    };
    return colors[status] || colors.draft;
  };

  return (
    <PayrollLayout title="Payroll Summary">
      <div className="flex flex-col gap-y-3 font-poppins">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
          <h2 className="text-xl font-semibold dark:text-white mb-4">
            Payroll Summary
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            View comprehensive payroll summary and statistics for the selected
            period.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">
                Year
              </label>
              <input
                type="number"
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                min="2020"
                max="2030"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium dark:text-white">
                Month
              </label>
              <select
                className="border rounded px-3 py-2 dark:bg-black dark:text-white"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={loadSummary}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? "Loading..." : "Load Summary"}
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mx-2">
              <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Total Employees
                    </p>
                    <p className="text-2xl font-bold dark:text-white">
                      {formatNumber(summary.total_employees || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                    <svg
                      className="w-6 h-6 text-blue-600 dark:text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Total Payroll
                    </p>
                    <p className="text-2xl font-bold dark:text-white">
                      {formatCurrency(summary.total_payroll || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
                    <svg
                      className="w-6 h-6 text-green-600 dark:text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Average Salary
                    </p>
                    <p className="text-2xl font-bold dark:text-white">
                      {formatCurrency(summary.average_salary || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-full">
                    <svg
                      className="w-6 h-6 text-purple-600 dark:text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 p-4 rounded-2xl shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Processed Records
                    </p>
                    <p className="text-2xl font-bold dark:text-white">
                      {formatNumber(summary.processed_records || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-full">
                    <svg
                      className="w-6 h-6 text-orange-600 dark:text-orange-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Breakdown */}
            <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
              <h3 className="text-lg font-semibold dark:text-white mb-4">
                Status Breakdown
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {summary.status_breakdown &&
                  Object.entries(summary.status_breakdown).map(
                    ([status, count]) => (
                      <div
                        key={status}
                        className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-2 ${getStatusColor(status)}`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </div>
                        <div className="text-2xl font-bold dark:text-white">
                          {formatNumber(count)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          records
                        </div>
                      </div>
                    ),
                  )}
              </div>
            </div>

            {/* Department Summary */}
            {summary.department_summary &&
              summary.department_summary.length > 0 && (
                <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
                  <h3 className="text-lg font-semibold dark:text-white mb-4">
                    Department Summary
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold dark:text-white">
                            Department
                          </th>
                          <th className="text-left px-3 py-2 font-semibold dark:text-white">
                            Employees
                          </th>
                          <th className="text-left px-3 py-2 font-semibold dark:text-white">
                            Total Payroll
                          </th>
                          <th className="text-left px-3 py-2 font-semibold dark:text-white">
                            Average Salary
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.department_summary.map((dept, index) => (
                          <tr
                            key={index}
                            className="border-t dark:border-gray-700"
                          >
                            <td className="px-3 py-2 dark:text-white">
                              {dept.department_name}
                            </td>
                            <td className="px-3 py-2 dark:text-white">
                              {formatNumber(dept.employee_count)}
                            </td>
                            <td className="px-3 py-2 dark:text-white">
                              {formatCurrency(dept.total_payroll)}
                            </td>
                            <td className="px-3 py-2 dark:text-white">
                              {formatCurrency(dept.average_salary)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            {/* Salary Range Distribution */}
            {summary.salary_ranges && summary.salary_ranges.length > 0 && (
              <div className="bg-white dark:bg-gray-900 p-4 mx-2 rounded-2xl shadow">
                <h3 className="text-lg font-semibold dark:text-white mb-4">
                  Salary Range Distribution
                </h3>
                <div className="space-y-3">
                  {summary.salary_ranges.map((range, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex justify-between text-sm dark:text-white mb-1">
                          <span>
                            {formatCurrency(range.min)} -{" "}
                            {formatCurrency(range.max)}
                          </span>
                          <span>{formatNumber(range.count)} employees</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${(range.count / summary.total_employees) * 100}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mx-2">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white dark:bg-gray-900 p-8 mx-2 rounded-2xl shadow text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              Loading summary...
            </p>
          </div>
        )}
      </div>
    </PayrollLayout>
  );
}
